import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Input, Select, Textarea, DatePicker, FileUpload, Stepper, Badge } from '@dreamtree-org/twreact-ui';
import { Wrench, ShieldCheck, CalendarClock, Hand } from 'lucide-react';
import { PageHeader, SectionCard, QueryState, KeyValue } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { uploadFile } from '@/lib/uploads';
import { CHANNEL_LABEL, PRIORITY, STORAGE_ACCESS, isValidOtp } from '../domain';
import { PriorityBadge, TicketStatusBadge, useAction, ActionResult, formatDateTime, OTP_HINT, PhotoTile, HandoverCode } from '../components';

const SLOTS = ['09:00–10:00', '11:00–12:00', '13:00–14:00', '15:30–16:30', '17:00–18:00'].map((s) => ({ value: s, label: s }));
const PRIORITY_OPTIONS = Object.entries(PRIORITY).map(([value, m]) => ({ value, label: m.label }));
const PROGRESS = ['open', 'triaged', 'pickup_scheduled', 'device_received', 'in_service'];

function slotStart(date, slot) {
  const d = new Date(date);
  const [h, m] = slot.slice(0, 5).split(':').map(Number);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

function PickupCard({ ticket, technicians, act, reload }) {
  const asked = ticket.requestedSlot && ticket.mode === 'pickup' ? ticket.requestedSlot : null;
  const [date, setDate] = useState(asked ? new Date(asked.date) : new Date());
  const [slot, setSlot] = useState('');
  const [tech, setTech] = useState(ticket.pickup ? ticket.pickup.technicianId : '');
  const [notes, setNotes] = useState('');
  const canSchedule = ['open', 'triaged', 'pickup_scheduled'].includes(ticket.status);

  const submit = async () => {
    if (!date || !slot) return;
    const res = await act.run('serviceTicketSchedulePickup', { id: ticket.id, scheduledAt: slotStart(date, slot), slot, technicianId: tech, notes }, 'Pickup scheduled; it now shows in the technician’s field jobs.');
    if (res) reload();
  };

  return (
    <SectionCard title="Pickup">
      {ticket.pickup ? (
        <KeyValue
          items={[
            { label: 'When', value: `${new Date(ticket.pickup.scheduledAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${ticket.pickup.slot}` },
            { label: 'Pickup by', value: ticket.pickupTechnicianName },
            ticket.pickup.notes ? { label: 'Notes', value: ticket.pickup.notes } : null,
          ]}
        />
      ) : (
        <p className="text-sm opacity-70">
          {ticket.mode === 'dropoff'
            ? `Drop-off: the customer brings the device to the counter${ticket.requestedSlot ? ` (${new Date(ticket.requestedSlot.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${ticket.requestedSlot.label})` : ''}.`
            : asked
              ? `Customer asked for ${new Date(asked.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${asked.label}. Assign an agent to confirm it.`
              : 'No pickup scheduled yet.'}
        </p>
      )}
      {canSchedule ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 pt-2 border-t border-[var(--app-border)]">
          <DatePicker label="Pickup date" value={date} onChange={setDate} minDate={new Date()} />
          <Select label="Time slot" options={SLOTS} value={slot} onChange={setSlot} closeOnSelect placeholder="Choose a slot" />
          <Select label="Pickup by" options={technicians.map((t) => ({ value: t.id, label: `${t.name} · ${t.zone}` }))} value={tech} onChange={setTech} closeOnSelect />
          <Input label="Notes for the agent" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, landmark…" />
          <div className="sm:col-span-2">
            <Button onClick={submit} disabled={!date || !slot || !tech} loading={act.busy === 'serviceTicketSchedulePickup'} leftIcon={<CalendarClock size={16} />}>
              {ticket.pickup ? 'Reschedule pickup' : 'Schedule pickup'}
            </Button>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

function CustodyCard({ ticket, act, reload }) {
  const [acceptedBy, setAcceptedBy] = useState('');
  const [condition, setCondition] = useState('');
  const [files, setFiles] = useState([]);
  const [otp, setOtp] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const handedOver = ticket.custody.some((e) => e.kind !== 'return');
  const ready = acceptedBy.trim() && condition.trim() && files.length && isValidOtp(otp);

  const submit = async () => {
    setUploadError('');
    setUploading(true);
    let media;
    try {
      media = await uploadFile(files[0], { ownerType: 'service_ticket', ownerId: ticket.recordId, kind: 'intake' });
    } catch (err) {
      setUploadError(err.message);
      return;
    } finally {
      setUploading(false);
    }
    const res = await act.run(
      'serviceTicketCustody',
      { id: ticket.id, acceptedBy, condition, mediaId: media.media_id, otp, kind: ticket.mode === 'dropoff' ? 'counter_intake' : 'pickup' },
      'Custody recorded. The device is now in the workshop.'
    );
    if (res) {
      setFiles([]);
      reload();
    }
  };

  return (
    <SectionCard title="Custody handover">
      {ticket.custody.length ? (
        <ol className="space-y-3">
          {ticket.custody.map((e) => (
            <li key={e.id} className="rounded-md border border-[var(--app-border)] p-3 space-y-1 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={e.kind === 'return' ? 'success' : 'primary'} size="sm">
                  {{ pickup: 'Pickup', counter_intake: 'Counter intake', return: 'Return' }[e.kind] || e.kind}
                </Badge>
                <span className="opacity-70">{formatDateTime(e.at)}</span>
                {e.otpAcknowledged ? (
                  <Badge variant="success" size="sm">
                    OTP acknowledged
                  </Badge>
                ) : null}
              </div>
              <KeyValue
                items={[
                  { label: 'From', value: e.from },
                  { label: 'Accepted by', value: e.acceptedBy },
                  { label: 'Condition', value: e.condition },
                  { label: 'Photo', value: e.photoUrl ? <a className="underline" href={e.photoUrl} target="_blank" rel="noreferrer">{e.photo || 'View'}</a> : e.photo },
                  { label: 'Acknowledgement', value: e.ackRef },
                ]}
              />
            </li>
          ))}
        </ol>
      ) : null}
      {!handedOver ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Accepted by" value={acceptedBy} onChange={(e) => setAcceptedBy(e.target.value)} placeholder="Name of the staff or agent" required />
          <Input
            label="Customer’s code"
            type="tel"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            helperText={OTP_HINT}
            required
          />
          <div className="sm:col-span-2">
            <Textarea label="Condition at handover" value={condition} onChange={(e) => setCondition(e.target.value)} rows={2} placeholder="Scratches, dents, accessories included (charger, bag)…" required />
          </div>
          <div className="sm:col-span-2 space-y-1">
            <div className="text-sm font-medium">Condition photo</div>
            <FileUpload accept="image/*" files={files} onFileSelect={(f) => setFiles(f.slice(0, 1))} onFileRemove={() => setFiles([])} maxSize={8 * 1024 * 1024} />
          </div>
          <div className="sm:col-span-2">
            <HandoverCode purpose="custody" id={ticket.id} />
          </div>
          {uploadError ? <p className="sm:col-span-2 text-sm text-error-600">{uploadError}</p> : null}
          <div className="sm:col-span-2">
            <Button onClick={submit} disabled={!ready} loading={uploading || act.busy === 'serviceTicketCustody'} leftIcon={<Hand size={16} />}>
              Record handover
            </Button>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

/** R-01/R-02 Ticket detail: reported problem, triage, pickup, custody and "Create work order". */
export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const q = useQuery('serviceTicket', { id }, { select: (r) => r.data });
  const techs = useQuery('serviceTechnicians', null, { select: (r) => r.data });
  const act = useAction();
  const [priority, setPriority] = useState('');
  const [assignee, setAssignee] = useState('');

  const createJob = async (t) => {
    const res = await act.run('serviceTicketCreateJob', { id: t.id, technicianId: assignee || null });
    if (res) nav(`/jobs/${res.data.id}/diagnosis`);
  };
  const triage = async (t) => {
    const res = await act.run('serviceTicketTriage', { id: t.id, priority: priority || t.priority }, 'Ticket triaged.');
    if (res) q.reload();
  };

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(t) => {
          const access = STORAGE_ACCESS[t.consent.storageAccess];
          const step = Math.max(0, PROGRESS.indexOf(t.status === 'closed' ? 'in_service' : t.status));
          return (
            <>
              <PageHeader
                title={`Ticket ${t.id}`}
                subtitle={`${t.customer.name} · ${t.device.model}`}
                back={{ label: 'Tickets', href: '/tickets' }}
                actions={
                  t.jobId ? (
                    <Button onClick={() => nav(`/jobs/${t.jobId}`)} leftIcon={<Wrench size={16} />}>
                      Open work order {t.jobId}
                    </Button>
                  ) : null
                }
              />
              <div className="overflow-x-auto">
                <Stepper
                  className="min-w-[36rem] py-2"
                  orientation="horizontal"
                  currentStep={step}
                  steps={[
                    { title: 'Reported' },
                    { title: 'Triaged' },
                    { title: 'Pickup scheduled' },
                    { title: 'Device received' },
                    { title: 'Work order' },
                  ].map((s, i) => ({ ...s, status: i < step ? 'completed' : i === step ? 'current' : 'upcoming' }))}
                />
              </div>
              <ActionResult action={act} />

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                  <SectionCard title="Reported problem" actions={<TicketStatusBadge status={t.status} />}>
                    <div className="space-y-1">
                      <div className="font-semibold">{t.problem}</div>
                      <p className="text-sm opacity-80">{t.description}</p>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {t.photos.map((p) => (
                        <PhotoTile key={p.id} label={p.label} url={p.url} />
                      ))}
                    </div>
                    <KeyValue
                      columns={2}
                      items={[
                        { label: 'Channel', value: CHANNEL_LABEL[t.channel] },
                        { label: 'Opened', value: formatDateTime(t.openedAt) },
                        { label: 'Device', value: `${t.device.brand} · ${t.device.model}` },
                        { label: 'Serial', value: <span className="font-mono">{t.device.serialMasked}</span> },
                      ]}
                    />
                  </SectionCard>
                  <PickupCard ticket={t} technicians={techs.data || []} act={act} reload={q.reload} />
                  <CustodyCard ticket={t} act={act} reload={q.reload} />
                </div>

                <div className="space-y-4">
                  <SectionCard title="Customer and location">
                    <KeyValue
                      items={[
                        { label: 'Name', value: t.customer.name },
                        { label: 'Phone', value: t.customer.phone },
                        { label: 'Address', value: t.location.address },
                        { label: 'PIN', value: t.location.pin },
                      ]}
                    />
                  </SectionCard>

                  <SectionCard title="Triage" actions={<PriorityBadge priority={t.priority} />}>
                    <Select label="Priority" options={PRIORITY_OPTIONS} value={priority || t.priority} onChange={setPriority} closeOnSelect />
                    <Button variant="outline" onClick={() => triage(t)} loading={act.busy === 'serviceTicketTriage'} disabled={t.status !== 'open' && (priority || t.priority) === t.priority}>
                      {t.status === 'open' ? 'Mark triaged' : 'Update priority'}
                    </Button>
                  </SectionCard>

                  <SectionCard title="Storage-access consent">
                    <div className="flex items-start gap-2 text-sm">
                      <ShieldCheck size={18} className="text-primary-600 shrink-0 mt-0.5" aria-hidden />
                      <div>
                        <div className="font-medium">{access.label}</div>
                        <div className="opacity-80">{access.detail}</div>
                      </div>
                    </div>
                    <KeyValue
                      items={[
                        { label: 'Wipe allowed', value: t.consent.dataWipeAllowed ? 'Yes' : 'No' },
                        { label: 'Backup requested', value: t.consent.backupRequested ? 'Yes' : 'No' },
                        { label: 'Consent version', value: t.consent.version },
                      ]}
                    />
                    <p className="text-xs opacity-70">{t.consent.credentialsPolicy}</p>
                  </SectionCard>

                  {!t.jobId ? (
                    <SectionCard title="Work order">
                      <p className="text-sm opacity-80">
                        {t.status === 'device_received'
                          ? 'The device is in custody. Create the repair work order to start diagnosis.'
                          : 'Available once the custody handover is recorded.'}
                      </p>
                      <Select
                        label="Assign technician (optional)"
                        options={(techs.data || []).map((x) => ({ value: x.id, label: `${x.name} · ${x.percent}% booked` }))}
                        value={assignee}
                        onChange={setAssignee}
                        closeOnSelect
                        allowClear
                      />
                      <Button fullWidth onClick={() => createJob(t)} disabled={t.status !== 'device_received'} loading={act.busy === 'serviceTicketCreateJob'} leftIcon={<Wrench size={16} />}>
                        Create work order
                      </Button>
                    </SectionCard>
                  ) : null}
                </div>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
